import { CacheService } from 'web_core_library';
import {
  IGroupInfo,
  IGroupInfoCacheValue,
  IGroupMemberStatsCacheValue,
  IGroupTrainingStatisticsApiResponsePayload,
} from './types';

export function getService(cacheService: typeof CacheService = CacheService) {
  let _userId = 0;

  async function init(userId: number) {
    _userId = userId;
    await cacheService.init();
  }

  function isReady() {
    return _userId !== 0;
  }

  function getGroupInfo() {
    if (!isReady()) {
      throw new Error('GroupCache service requires userId to be set!');
    }
    return cacheService.load<IGroupInfoCacheValue>(`${_userId}-group-info`);
  }

  function setGroupInfo(group: IGroupInfo | undefined, timestamp: number = Date.now()) {
    if (!isReady()) {
      throw new Error('GroupCache service requires userId to be set!');
    }
    const cacheValue: IGroupInfoCacheValue = {
      group,
      timestamp,
    };
    return cacheService.save(`${_userId}-group-info`, cacheValue);
  }

  function getGroupMemberStats() {
    if (!isReady()) {
      throw new Error('GroupCache service requires userId to be set!');
    }
    return cacheService.load<IGroupMemberStatsCacheValue>(`${_userId}-group-member-stats`);
  }

  function setGroupMemberStats(
    stats: IGroupTrainingStatisticsApiResponsePayload | undefined,
    timestamp: number = Date.now()
  ) {
    if (!isReady()) {
      throw new Error('GroupCache service requires userId to be set!');
    }
    const cacheValue: IGroupMemberStatsCacheValue = {
      stats,
      timestamp,
    };
    return cacheService.save(`${_userId}-group-member-stats`, cacheValue);
  }

  return {
    init,
    isReady,
    getGroupInfo,
    setGroupInfo,
    getGroupMemberStats,
    setGroupMemberStats,
  };
}

export const GROUP_CACHE_EXPIRATION_TIMEOUT = 24 * 60 * 60 * 1000; // 24 hours

const isCacheExpired = (syncTimestamp?: number) => {
  if (!syncTimestamp) {
    return true;
  }
  const current = Date.now();
  return current - syncTimestamp >= GROUP_CACHE_EXPIRATION_TIMEOUT;
};

export function isCacheValueInvalid(value: IGroupInfoCacheValue | undefined) {
  const { group, timestamp } = value || {};
  return isCacheExpired(timestamp) || typeof group === 'undefined';
}

export function isStatCacheValueInvalid(value: IGroupMemberStatsCacheValue | undefined) {
  const { stats, timestamp } = value || {};
  return isCacheExpired(timestamp) || typeof stats === 'undefined';
}

export type TGroupCacheService = ReturnType<typeof getService>;
const GroupService = getService();
export default GroupService;
